import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFWmsEntities,
} from '@pf/shared-common';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withFragmentSearchFilter,
    withMetadata,
    withSearchFilter,
} from '@pf/shared-utility';
import {
    ShipmentCreate,
    ShipmentDto,
    ShipmentRead,
    ShipmentSearchParams,
} from '../../entities/shipment.dto';
import {
    CurrencySymbol,
    CustomFieldMetadataMap,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';
import { withWarehouseSearchFilter } from '../wms-mapping.util';

const symbols = entitySymbols(PFWmsEntities.Shipment);

withDefaultEntityMetadata<ShipmentDto>(symbols.dto, {
    resourceAuth: ResourceAuthSymbol,
    warehouse: Object,
    status: String,
    bolNumber: String,
    trailerNumber: String,
    trackingNumber: String,
    externalReferenceId: String,
    expectedDeliveryDate: String,
    actualDeliveryDate: String,
    expectedPickupDate: String,
    actualPickupDate: String,
    shipmentValue: CurrencySymbol,
    shipTo: Object,
    shipToContact: Object,
    customFields: [CustomFieldMetadataMap.symbols.dto],
    packingLists: [Object],
    loads: [String],
    carrier: Object,
    shipmentType: Object,
});

withDefaultEntityMetadata<ShipmentRead>(symbols.entity, {
    resourceAuth: ResourceAuthSymbol,
    warehouse: Object,
    status: String,
    bolNumber: String,
    trailerNumber: String,
    trackingNumber: String,
    externalReferenceId: String,
    expectedDeliveryDate: String,
    actualDeliveryDate: String,
    expectedPickupDate: String,
    actualPickupDate: String,
    shipmentValue: CurrencySymbol,
    shipTo: Object,
    shipToContact: Object,
    customFields: [CustomFieldMetadataMap.symbols.dto],
    packingLists: [Object],
    loads: [String],
    carrier: Object,
    shipmentType: Object,
});

withMetadata<ShipmentCreate>(symbols.createBody, {
    resourceAuth: ResourceAuthSymbol,
    warehouseId: String,
    status: String,
    bolNumber: String,
    trailerNumber: String,
    trackingNumber: String,
    externalReferenceId: String,
    expectedDeliveryDate: String,
    actualDeliveryDate: String,
    expectedPickupDate: String,
    actualPickupDate: String,
    shipmentValue: CurrencySymbol,
    shipTo: Object,
    shipToContact: Object,
    loadIds: [String],
    carrierId: String,
    shipmentTypeId: String,
});

withDefaultSearchParamsMetadata<ShipmentSearchParams>(symbols.searchParams, {
    status: String,
    warehouseId: String,
    carrierId: String,
    trailerNumber: String,
    bolNumber: String,
    trackingNumber: String,
    deliveryLocationId: String,
    externalReferenceId: String,
    expectedDeliveryStartDate: String,
    expectedDeliveryEndDate: String,
    actualDeliveryStartDate: String,
    actualDeliveryEndDate: String,
    expectedPickupStartDate: String,
    expectedPickupEndDate: String,
    actualPickupStartDate: String,
    actualPickupEndDate: String,
    shipmentTypeIds: [String],
    shipToSourceEntity: String,
    shipToSourceEntityId: String,
});

export const ShipmentMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<ShipmentRead, ShipmentDto>(
            mapper,
            symbols.entity,
            symbols.dto
        );
        createMap<ShipmentDto, ShipmentRead>(
            mapper,
            symbols.dto,
            symbols.entity
        );
        createMap<ShipmentDto, ShipmentCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                d => d.warehouseId,
                mapFrom(s => s.warehouse.id)
            ),
            forMember(
                d => d.carrierId,
                mapFrom(s => s.carrier?.id)
            ),
            forMember(
                d => d.shipmentTypeId,
                mapFrom(s => s.shipmentType?.id)
            )
        );
        createMap<IQueryParams<ShipmentDto>, ShipmentSearchParams>(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withSearchFilter('status'),
            withWarehouseSearchFilter(),
            withFragmentSearchFilter('carrier'),
            withSearchFilter('trailerNumber'),
            withSearchFilter('bolNumber'),
            withSearchFilter('trackingNumber'),
            ...withDefaultSearchParamsMappingConfiguration()
        );
    },
};
